import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Package, CheckCircle2 } from "lucide-react";

function resolveApiUrl() {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
  if (!envApiUrl) return fallback;
  try {
    const u = new URL(envApiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
    return envApiUrl;
  } catch {
    return envApiUrl || fallback;
  }
}

const MALAYSIA_TZ = "Asia/Kuala_Lumpur";

function malaysiaDateKeyFromDate(d: Date) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: MALAYSIA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function malaysiaDateLabelFromDate(d: Date) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: MALAYSIA_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const label = fmt.format(d);
  return label.replace(/\s+/g, "-");
}

function malaysiaWeekdayAbbrFromDate(d: Date) {
  const fmt = new Intl.DateTimeFormat("en-US", { timeZone: MALAYSIA_TZ, weekday: "short" });
  return fmt.format(d);
}

function malaysiaDateLabelWithWeekdayFromDate(d: Date) {
  return `${malaysiaDateLabelFromDate(d)} (${malaysiaWeekdayAbbrFromDate(d)})`;
}

function dateKeyToUTCDate(key: string) {
  const [y, m, dd] = key.split("-").map((x) => Number(x));
  return new Date(Date.UTC(y, m - 1, dd));
}

type SalesProductRow = {
  productId: string;
  name: string;
  sku: string | null;
  qtySold: number;
  sellingPrice: number | null;
  productImageUrl?: string | null;
  value: number | null;
};

type SalesGroupRow = { groupName: string; products: SalesProductRow[] };
type SalesSubmissionInfo = {
  isSubmitted: boolean;
  bankedAt: string | null;
  expectedTotal: number;
  remainingAmount: number;
};
type TeamSalesResponse = {
  team: { id: string; name: string } | null;
  groups: SalesGroupRow[];
  salesSubmission?: SalesSubmissionInfo;
};

export default function SalesHistory() {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => resolveApiUrl(), []);
  const token = localStorage.getItem("token") || "";

  const todayDateKey = useMemo(() => malaysiaDateKeyFromDate(new Date()), []);

  const [selectedDateKey, setSelectedDateKey] = useState<string>(todayDateKey);
  const [searchQuery, setSearchQuery] = useState("");
  const [productGroupNames, setProductGroupNames] = useState<string[]>([]);
  const [activeGroupName, setActiveGroupName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [salesData, setSalesData] = useState<TeamSalesResponse | null>(null);
  const [brokenImageProductIds, setBrokenImageProductIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadGroups = async () => {
      if (!token) {
        setError("Not authenticated. Please login again.");
        return;
      }
      try {
        const res = await fetch(`${apiUrl}/supervisor/product-groups`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: { groups: string[] } = await res.json().catch(() => ({ groups: [] }));
        if (!res.ok) return;
        const groups = json.groups ?? [];
        setProductGroupNames(groups);
        setActiveGroupName((prev) => (groups.includes(prev) ? prev : groups[0] ?? ""));
      } catch {
        // Ignore; the group tabs will just stay empty.
      }
    };
    void loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedDateKey) return;
    const loadSales = async () => {
      if (!token) return;
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-sales?date=${selectedDateKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: TeamSalesResponse = await res.json().catch(() => ({ team: null, groups: [] }));
        if (!res.ok) {
          setError((json as any)?.message || `Failed to load sales (${res.status}).`);
          return;
        }
        setSalesData(json);
        setBrokenImageProductIds({});
      } catch {
        setError("Unable to connect to server. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    void loadSales();
  }, [apiUrl, token, selectedDateKey]);

  const filteredProducts = useMemo(() => {
    const group = (salesData?.groups ?? []).find((g) => g.groupName === activeGroupName);
    const products = group?.products ?? [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => p.name.toLowerCase().includes(q) || (p.sku ?? "").toLowerCase().includes(q));
  }, [salesData, activeGroupName, searchQuery]);

  const totals = useMemo(() => {
    let totalQty = 0;
    let totalValue = 0;
    for (const p of filteredProducts) {
      totalQty += p.qtySold ?? 0;
      if (typeof p.sellingPrice === "number") totalValue += (p.qtySold ?? 0) * p.sellingPrice;
    }
    return { totalQty, totalValue };
  }, [filteredProducts]);

  const selectedDateLabel = malaysiaDateLabelWithWeekdayFromDate(dateKeyToUTCDate(selectedDateKey));
  const isToday = selectedDateKey === todayDateKey;
  const isAtNewestAllowedDate = isToday;

  const salesSubmission = salesData?.salesSubmission;
  const isSubmitted = Boolean(salesSubmission?.isSubmitted);
  const remainingAmount = salesSubmission?.remainingAmount ?? 0;
  const isFullySettled = isSubmitted && remainingAmount <= 0.004;

  const shiftSelectedDate = (deltaDays: number) => {
    setSelectedDateKey((prev) => {
      const base = dateKeyToUTCDate(prev);
      base.setUTCDate(base.getUTCDate() + deltaDays);
      const nextKey = malaysiaDateKeyFromDate(base);
      return nextKey > todayDateKey ? todayDateKey : nextKey;
    });
  };

  return (
    <div className="min-h-[calc(100vh-60px)] bg-white">
      {/* Gradient Header */}
      <div className="h-[100px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate("/app")}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Back"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>

            <div className="text-[20px] font-semibold">Sales History</div>

            <div className="w-10 h-10" />
          </div>
        </div>
      </div>

      <div className="pt-3" />

      {/* Date bar */}
      <div className="px-4 pt-1 pb-1">
        <div className="bg-white border border-gray-200 rounded-xl px-2 py-1 flex items-center justify-between">
          <button
            type="button"
            aria-label="Previous date"
            onClick={() => shiftSelectedDate(-1)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 bg-white text-sm text-slate-700 hover:bg-gray-50"
          >
            <span className="font-semibold leading-none">&lt;</span>
          </button>

          <div className="flex-1 flex justify-center px-2 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate leading-4">
              {selectedDateLabel}
              {isToday ? <span className="font-normal text-slate-500"> (Today)</span> : null}
            </div>
          </div>

          <button
            type="button"
            aria-label="Next date (toward today)"
            disabled={isAtNewestAllowedDate}
            onClick={() => shiftSelectedDate(1)}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-md border ${
              isAtNewestAllowedDate
                ? "border-gray-200 bg-white text-sm text-slate-400 cursor-not-allowed"
                : "border-gray-200 bg-white text-slate-700 hover:bg-gray-50"
            }`}
          >
            <span className="font-semibold leading-none">&gt;</span>
          </button>
        </div>
      </div>

      {/* Sales submission status */}
      {!loading && salesData ? (
        <div className="px-4 pt-2">
          {isSubmitted ? (
            <div
              className={`rounded-lg border px-3 py-2 text-sm font-medium flex items-center justify-between gap-2 ${
                isFullySettled ? "border-green-200 bg-green-50 text-green-800" : "border-amber-200 bg-amber-50 text-amber-800"
              }`}
            >
              <span className="inline-flex items-center gap-1.5 min-w-0">
                <CheckCircle2
                  size={16}
                  className={isFullySettled ? "text-green-700 shrink-0" : "text-amber-700 shrink-0"}
                />
                <span className="truncate">
                  Sales submitted
                  {isFullySettled ? (
                    <span className="ml-1.5 font-semibold">· Fully settled</span>
                  ) : (
                    <span className="ml-1.5 font-semibold">
                      · Difference: RM{" "}
                      {remainingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                </span>
              </span>
              <button
                type="button"
                className={`shrink-0 rounded-md border bg-white px-2.5 py-1 text-xs font-semibold ${
                  isFullySettled ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"
                }`}
                onClick={() => navigate(`/app/sales/confirm?date=${selectedDateKey}`)}
              >
                View
              </button>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-600">
              Sales not submitted
            </div>
          )}
        </div>
      ) : null}

      {/* Tab Buttons (ProductGroup.groupName) */}
      <div className="px-4 pt-0 pb-1 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 flex items-center bg-white rounded-lg border border-gray-200 h-9 px-2">
                <Search size={16} className="text-gray-400 mr-1.5 shrink-0" />
                <input
                  type="text"
                  className="flex-1 outline-none border-0 shadow-none rounded-none p-0 appearance-none text-sm text-slate-900 placeholder:text-gray-400 bg-transparent min-w-0"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div
              className="bg-gray-100 rounded-lg p-[4px] overflow-x-auto
              [&::-webkit-scrollbar]:h-1
              [&::-webkit-scrollbar-track]:bg-transparent
              [&::-webkit-scrollbar-thumb]:rounded-full
              [&::-webkit-scrollbar-thumb]:bg-theme-1/45
              hover:[&::-webkit-scrollbar-thumb]:bg-theme-1/60"
            >
              <div className="flex w-max min-w-full gap-1">
                {productGroupNames.map((groupName) => {
                  const active = groupName === activeGroupName;
                  return (
                    <button
                      key={groupName}
                      type="button"
                      className={`shrink-0 py-2 px-3 rounded-md text-sm font-medium whitespace-nowrap ${
                        active ? "bg-white text-primary shadow-[0_2px_4px_rgba(0,0,0,0.08)]" : "text-gray-600"
                      }`}
                      onClick={() => setActiveGroupName(groupName)}
                    >
                      {groupName}
                    </button>
                  );
                })}
                {productGroupNames.length === 0 ? <div className="py-2 px-3 text-xs text-gray-500">No groups</div> : null}
              </div>
            </div>
          </div>

          {error ? (
            <div className="px-4 pt-2">
              <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm mb-2">{error}</div>
            </div>
          ) : null}

          {loading ? (
            <div className="bg-gray-50 px-4 pt-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-12 rounded-lg bg-white border border-gray-200 animate-pulse mb-2" />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 px-4 pt-2 pb-24">
              {filteredProducts.length ? (
                <div className="pb-4">
                  {filteredProducts.map((p) => {
                    const sellingPrice = p.sellingPrice ?? null;
                    const value = typeof sellingPrice === "number" ? (p.qtySold ?? 0) * sellingPrice : null;
                    const showImage = Boolean(p.productImageUrl) && !brokenImageProductIds[p.productId];

                    return (
                      <div
                        key={p.productId}
                        className="rounded-xl border border-primary/40 shadow-[0_2px_8px_rgba(0,0,0,0.08)] mb-2 overflow-hidden bg-white"
                      >
                        <div className="flex flex-row p-3 bg-white">
                          <div className="w-[50px] h-[50px] rounded-lg bg-primary/5 border border-primary/30 flex items-center justify-center shrink-0">
                            {showImage ? (
                              <img
                                src={p.productImageUrl!}
                                alt={p.name}
                                loading="lazy"
                                className="w-full h-full rounded-lg object-contain bg-white"
                                onError={() =>
                                  setBrokenImageProductIds((prev) => ({ ...prev, [p.productId]: true }))
                                }
                              />
                            ) : (
                              <Package size={20} className="text-gray-400" />
                            )}
                          </div>

                          <div className="flex-1 ml-3 mr-3 flex flex-col justify-between min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-900 leading-5 line-clamp-3">{p.name}</div>
                                {p.sku ? (
                                  <div className="mt-1 text-xs text-slate-500">
                                    SKU: <span className="text-xs font-medium text-slate-700">{p.sku}</span>
                                  </div>
                                ) : null}
                                <div className="mt-2 text-[12px] font-semibold text-slate-900 tabular-nums">
                                  {typeof sellingPrice === "number"
                                    ? `RM ${sellingPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : "—"}
                                </div>
                              </div>

                              <div className="flex items-start gap-2 ml-auto">
                                <div className="flex flex-col items-center min-w-[60px] shrink-0 gap-1">
                                  <div className="h-3 flex items-center whitespace-nowrap text-[11px] font-medium text-slate-600 leading-3">
                                    Qty Sold
                                  </div>
                                  <div className="h-10 w-[42px] flex items-center justify-center text-sm font-semibold text-slate-900 tabular-nums">
                                    {(p.qtySold ?? 0).toLocaleString()}
                                  </div>
                                </div>

                                <div className="flex flex-col items-end text-right gap-1">
                                  <div className="h-3 flex items-center whitespace-nowrap text-[11px] font-medium text-slate-600 leading-3">
                                    Value (RM)
                                  </div>
                                  <div className="h-10 flex items-center justify-end text-sm font-semibold text-slate-900 tabular-nums">
                                    {typeof value === "number"
                                      ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                      : "—"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Totals bar: keep visible above fixed bottom nav */}
                  <div className="fixed bottom-[60px] left-0 right-0 mx-auto w-full max-w-md z-[60] mt-0 bg-blue-100/90 backdrop-blur supports-[backdrop-filter]:bg-blue-100/80 border-t border-blue-300/70 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
                    <div className="px-3 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold text-slate-600 leading-3">Total Pack Sold</div>
                        <div className="text-base font-semibold text-slate-900 tabular-nums leading-5">
                          {totals.totalQty.toLocaleString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[11px] font-semibold text-slate-600 leading-3">Total Value (RM)</div>
                        <div className="text-base font-semibold text-slate-900 tabular-nums leading-5">
                          {`RM ${totals.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center py-10">
                  <Package size={48} className="text-gray-400" />
                  <div className="mt-2 text-sm font-medium text-slate-700">No products found</div>
                  {searchQuery ? <div className="mt-1 text-xs text-slate-500">Try adjusting your search</div> : null}
                </div>
              )}
            </div>
          )}
    </div>
  );
}
